import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { TooltipTerm } from '../ui/Tooltip';
import { fmt } from '../../utils/formatters';
import { RiskMetricsService } from '../../services/riskMetrics';
import { ATHService, ATHCache } from '../../services/athService';
import { TokenPriceHistoryService } from '../../services/tokenPriceHistory';
import { useToast } from '../Toast';
import type { Token, Prices, FIFOResults } from '../../types';

interface RiskAnalysisProps {
  tokens: Token[];
  prices: Prices;
  fifoResults: FIFOResults;
}

/**
 * RiskAnalysis Component
 * Affiche l'analyse de risque du portfolio avec données ATH/Min2Y et MA200
 */
export function RiskAnalysis({ tokens, prices, fifoResults }: RiskAnalysisProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [athData, setAthData] = useState<ATHCache>({});
  const [maEmaData, setMaEmaData] = useState<Record<string, { ma200: number | null; ema200: number | null }>>({});
  const [loadingATH, setLoadingATH] = useState(false);
  const [loadingMAEMA, setLoadingMAEMA] = useState(false);
  const toast = useToast();

  // Map color names to actual CSS values
  const getColorValue = (colorName: string): string => {
    const colorMap: Record<string, string> = {
      'green': '#10b981',    // emerald-500
      'blue': '#3b82f6',     // blue-500
      'orange': '#f59e0b',   // amber-500
      'red': '#ef4444'       // red-500
    };
    return colorMap[colorName] || '#6366f1'; // fallback to primary
  };

  // Load ATH data
  const loadATHData = async (forceRefresh: boolean = false) => {
    if (tokens.length === 0) return;

    setLoadingATH(true);
    const coinIds = tokens.map(t => t.id);

    try {
      const result = await ATHService.getATHMinForTokensBatch(coinIds, 250, 300, forceRefresh);
      setAthData(result.data);
      setLoadingATH(false);

      if (forceRefresh) {
        toast(t('riskAnalysis.athDataUpdated'), 'success', 3000);
      }
    } catch (error) {
      setLoadingATH(false);
      console.error('Error loading ATH data:', error);
    }
  };

  // Load MA200 and EMA200 data (optimized - single fetch)
  const loadMAEMAData = async () => {
    if (tokens.length === 0) return;

    setLoadingMAEMA(true);
    const coinIds = tokens.map(t => t.id);

    try {
      const result = await TokenPriceHistoryService.getMA200AndEMA200ForMultipleTokens(coinIds);
      setMaEmaData(result);
      setLoadingMAEMA(false);
    } catch (error) {
      setLoadingMAEMA(false);
      console.error('Error loading MA/EMA data:', error);
    }
  };

  // Load data when component mounts or tokens change
  useEffect(() => {
    loadATHData(false);
    loadMAEMAData();
  }, [tokens]);

  if (tokens.length === 0) return null;

  const riskMetrics = RiskMetricsService.calculatePortfolioRiskMetrics(tokens, prices, fifoResults);
  const volatilityInfo = RiskMetricsService.getVolatilityLevel(riskMetrics.weightedVolatility);
  const diversificationInfo = RiskMetricsService.getDiversificationLevel(riskMetrics.diversificationScore);

  // Helper to calculate ATH metrics for a token
  const getATHMetrics = (tokenId: string, currentPrice: number) => {
    const data = athData[tokenId];
    const maEma = maEmaData[tokenId];
    const ma200 = maEma?.ma200;
    const ema200 = maEma?.ema200;

    if (!data) return null;

    const ath = data.ath;
    const min2Y = data.min2Y;

    let vsAth: number | null = null;
    let vsMin2Y: number | null = null;
    let vsMA200: number | null = null;
    let vsEMA200: number | null = null;
    let signal = '⚪';
    let athClass = '';
    let minClass = '';

    if (ath && ath > 0) {
      vsAth = ((currentPrice - ath) / ath) * 100;
      if (vsAth >= -15 && vsAth <= 0) {
        athClass = 'near-ath';
        signal = '🔴';
      }
    }

    if (min2Y && min2Y > 0) {
      vsMin2Y = ((currentPrice - min2Y) / min2Y) * 100;
      if (vsMin2Y >= 0 && vsMin2Y <= 15) {
        minClass = 'near-min';
        signal = '🟢';
      }
    }

    if (ma200 && ma200 > 0) {
      vsMA200 = ((currentPrice - ma200) / ma200) * 100;
    }

    if (ema200 && ema200 > 0) {
      vsEMA200 = ((currentPrice - ema200) / ema200) * 100;
    }

    if (athClass && minClass) {
      signal = '🟡';
    }

    return { ath, min2Y, vsAth, vsMin2Y, vsMA200, vsEMA200, signal, athClass, minClass };
  };

  return (
    <div className="card ath-card" style={{ marginBottom: '20px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer'
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 style={{ fontSize: '16px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.Target size={18} />
          {t('riskAnalysis.title')}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Refresh ATH button - only visible when expanded */}
          {isExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                loadATHData(true);
              }}
              disabled={loadingATH}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: loadingATH ? 'not-allowed' : 'pointer',
                opacity: loadingATH ? 0.5 : 1
              }}
              title={t('riskAnalysis.refreshAthData')}
            >
              <Icons.RefreshCw size={12} />
              ATH
            </button>
          )}
          <div style={{ fontSize: '12px', color: 'var(--muted)', textAlign: 'right' }}>
            {riskMetrics.numberOfHoldings} holdings
          </div>
          {isExpanded ? <Icons.ChevronUp size={18} /> : <Icons.ChevronDown size={18} />}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div style={{ marginTop: '16px' }}>
          {/* Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: 'var(--bg-secondary)',
              border: `2px solid ${getColorValue(diversificationInfo.color)}`
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>
                <TooltipTerm content={t('riskAnalysis.diversificationTooltip')}>
                  {t('riskAnalysis.diversification')}
                </TooltipTerm>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: getColorValue(diversificationInfo.color) }}>
                {fmt(riskMetrics.diversificationScore, 0)}%
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {diversificationInfo.label}
              </div>
            </div>

            <div style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: 'var(--bg-secondary)',
              border: `2px solid ${getColorValue(volatilityInfo.color)}`
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>
                <TooltipTerm content={t('riskAnalysis.weightedVolatilityTooltip')}>
                  {t('riskAnalysis.weightedVolatility')}
                </TooltipTerm>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: getColorValue(volatilityInfo.color) }}>
                {fmt(riskMetrics.weightedVolatility, 1)}%
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {volatilityInfo.label}
              </div>
            </div>

            <div style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: 'var(--bg-secondary)',
              border: '2px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>
                <TooltipTerm content={t('riskAnalysis.largestPositionTooltip')}>
                  {t('riskAnalysis.largestPosition')}
                </TooltipTerm>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>
                {fmt(riskMetrics.largestExposure, 1)}%
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {riskMetrics.tokens[0]?.displayName || '-'}
              </div>
            </div>

            <div style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: 'var(--bg-secondary)',
              border: '2px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>
                <TooltipTerm content={t('riskAnalysis.herfindahlTooltip')}>
                  {t('riskAnalysis.herfindahlIndex')}
                </TooltipTerm>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>
                {fmt(riskMetrics.herfindahlIndex, 3)}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {riskMetrics.herfindahlIndex < 0.15 ? t('riskAnalysis.wellDiversified') : riskMetrics.herfindahlIndex < 0.25 ? t('riskAnalysis.moderatelyDiversified') : t('riskAnalysis.concentrated')}
              </div>
            </div>
          </div>

          {/* Token Breakdown Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Token</th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.exposureTooltip')}>{t('riskAnalysis.exposure')}</TooltipTerm>
                  </th>
                  <th style={{ padding: '6px 8px' }}>{t('portfolioTable.value')}</th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.athTooltip')}>ATH</TooltipTerm>
                  </th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.vsAthTooltip')}>vs ATH</TooltipTerm>
                  </th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.min2yTooltip')}>Min 2Y</TooltipTerm>
                  </th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.vsMin2yTooltip')}>vs Min2Y</TooltipTerm>
                  </th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.ma200Tooltip')}>% 200MA</TooltipTerm>
                  </th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.ema200Tooltip')}>% 200EMA</TooltipTerm>
                  </th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.signalTooltip')}>{t('riskAnalysis.signal')}</TooltipTerm>
                  </th>
                  <th style={{ padding: '6px 8px' }}>
                    <TooltipTerm content={t('riskAnalysis.riskTooltip')}>{t('riskAnalysis.risk')}</TooltipTerm>
                  </th>
                </tr>
              </thead>
              <tbody>
                {riskMetrics.tokens.map((token) => {
                  const volInfo = RiskMetricsService.getVolatilityLevel(token.volatility);
                  const barWidth = token.exposure;
                  const currentPrice = prices[token.tokenId]?.usd || 0;
                  const athMetrics = getATHMetrics(token.tokenId, currentPrice);

                  return (
                    <tr key={token.tokenId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ fontWeight: 600, padding: '6px 8px' }}>{token.displayName}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1, height: '6px', backgroundColor: 'var(--bg-secondary)', borderRadius: '3px', position: 'relative', maxWidth: '80px' }}>
                            <div
                              style={{
                                height: '100%',
                                width: `${barWidth}%`,
                                backgroundColor: getColorValue(volInfo.color),
                                borderRadius: '3px',
                                transition: 'width 0.3s ease'
                              }}
                            />
                          </div>
                          <span style={{ fontWeight: 600, minWidth: '40px', fontSize: '11px' }}>{fmt(token.exposure, 1)}%</span>
                        </div>
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '11px' }}>{fmt(token.value, 0)} $</td>
                      <td style={{ padding: '6px 8px', fontSize: '11px', textAlign: 'center' }}>
                        {loadingATH ? '⏳' : athMetrics?.ath ? `${fmt(athMetrics.ath, 4)} $` : '-'}
                      </td>
                      <td style={{
                        padding: '6px 8px',
                        fontSize: '11px',
                        textAlign: 'center',
                        background: athMetrics?.athClass === 'near-ath' ? '#fee' : 'transparent',
                        color: athMetrics?.athClass === 'near-ath' ? '#c53030' : 'inherit',
                        fontWeight: athMetrics?.athClass === 'near-ath' ? 700 : 400
                      }}>
                        {loadingATH ? '⏳' : athMetrics?.vsAth !== null && athMetrics?.vsAth !== undefined
                          ? `${athMetrics.vsAth >= 0 ? '+' : ''}${fmt(athMetrics.vsAth, 1)}%`
                          : '-'}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '11px', textAlign: 'center' }}>
                        {loadingATH ? '⏳' : athMetrics?.min2Y ? `${fmt(athMetrics.min2Y, 4)} $` : '-'}
                      </td>
                      <td style={{
                        padding: '6px 8px',
                        fontSize: '11px',
                        textAlign: 'center',
                        background: athMetrics?.minClass === 'near-min' ? '#efe' : 'transparent',
                        color: athMetrics?.minClass === 'near-min' ? '#16a34a' : (athMetrics?.vsMin2Y !== null && athMetrics?.vsMin2Y !== undefined ? '#16a34a' : 'inherit'),
                        fontWeight: athMetrics?.minClass === 'near-min' ? 700 : 400
                      }}>
                        {loadingATH ? '⏳' : athMetrics?.vsMin2Y !== null && athMetrics?.vsMin2Y !== undefined
                          ? `+${fmt(athMetrics.vsMin2Y, 1)}%`
                          : '-'}
                      </td>
                      <td style={{
                        padding: '6px 8px',
                        fontSize: '11px',
                        textAlign: 'center',
                        color: athMetrics?.vsMA200 !== null && athMetrics?.vsMA200 !== undefined
                          ? (athMetrics.vsMA200 >= 0 ? '#16a34a' : '#dc2626')
                          : 'var(--muted)',
                        fontWeight: athMetrics?.vsMA200 !== null && athMetrics?.vsMA200 !== undefined ? 600 : 400
                      }}>
                        {loadingMAEMA ? '⏳' : athMetrics?.vsMA200 !== null && athMetrics?.vsMA200 !== undefined
                          ? `${athMetrics.vsMA200 >= 0 ? '+' : ''}${fmt(athMetrics.vsMA200, 1)}%`
                          : '-'}
                      </td>
                      <td style={{
                        padding: '6px 8px',
                        fontSize: '11px',
                        textAlign: 'center',
                        color: athMetrics?.vsEMA200 !== null && athMetrics?.vsEMA200 !== undefined
                          ? (athMetrics.vsEMA200 >= 0 ? '#16a34a' : '#dc2626')
                          : 'var(--muted)',
                        fontWeight: athMetrics?.vsEMA200 !== null && athMetrics?.vsEMA200 !== undefined ? 600 : 400
                      }}>
                        {loadingMAEMA ? '⏳' : athMetrics?.vsEMA200 !== null && athMetrics?.vsEMA200 !== undefined
                          ? `${athMetrics.vsEMA200 >= 0 ? '+' : ''}${fmt(athMetrics.vsEMA200, 1)}%`
                          : '-'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '14px' }}>
                        {loadingATH ? '⏳' : athMetrics?.signal || '⚪'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <span
                          className={`badge-${volInfo.level === 'low' ? 'success' : volInfo.level === 'medium' ? 'info' : 'danger'}`}
                          style={{ fontSize: '10px', padding: '2px 6px' }}
                        >
                          {volInfo.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: 'var(--bg-secondary)',
            fontSize: '11px',
            color: 'var(--muted)'
          }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px' }}>🟢</span>
                <span>{t('riskAnalysis.nearMin2y')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px' }}>🔴</span>
                <span>{t('riskAnalysis.nearAth')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px' }}>🟡</span>
                <span>{t('riskAnalysis.mixedZone')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px' }}>⚪</span>
                <span>{t('riskAnalysis.neutral')}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <Icons.Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>{t('riskAnalysis.aboutMetrics')}</strong>
                <ul style={{ margin: '6px 0 0 0', paddingLeft: '16px' }}>
                  <li><strong>{t('riskAnalysis.diversification')}</strong> : {t('riskAnalysis.diversificationDesc')}</li>
                  <li><strong>ATH</strong> : {t('riskAnalysis.athDesc')}</li>
                  <li><strong>Min 2Y</strong> : {t('riskAnalysis.min2yDesc')}</li>
                  <li><strong>% 200MA</strong> : {t('riskAnalysis.ma200Desc')}</li>
                  <li><strong>% 200EMA</strong> : {t('riskAnalysis.ema200Desc')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
